import { ACTIVE_SCREEN_KEY, Store, getScreenData, setScreenData } from 'cw-ui-core';
import _ from 'lodash';
import { toArray, toMap } from 'src/util/Util';
import {
    DESELECTED_STATE,
    LEGACY_PERMISSION_SUPPORT_LEVEL,
    PARTIAL_SELECTED_STATE,
    PLATFORM_PERMISSION_SUPPORT_LEVEL,
    SELECTED_STATE,
} from 'src/features/application-roles/components/GranularPremission/constants';

export const checkIfAllPermissionsSelected = (domainId, screenKey = ACTIVE_SCREEN_KEY) => {
    const selectedPermissionsMap = toMap(
        getScreenData(Store().getState().screen, ['domains', domainId, 'selectedPermissions'], screenKey)
    );
    const selectedPermissionKeys = selectedPermissionsMap ? Object.keys(selectedPermissionsMap) : [];
    const allSelected = _.every(selectedPermissionKeys, permId => {
        return _.get(selectedPermissionsMap, [permId, 'value']) === 2;
    });

    Store().dispatch(setScreenData(['domains', domainId, 'isAllPermissionsSelected'], allSelected, screenKey));

    return allSelected;
};

export const getSelectedPermissions = (screenKey = ACTIVE_SCREEN_KEY) => {
    const domains = toArray(getScreenData(Store().getState().screen, ['domains'], screenKey));

    let selectedLegacyPermissions = [];
    let selectedPlatformPermissions = [];
    _.forEach(domains, domain => {
        const selectedPermissions = _.get(domain, 'selectedPermissions', {});
        const selectedPermissionsKeys = Object.keys(selectedPermissions);
        const supportLevel = _.get(domain, ['supportLevel']);
        const filteredSelectedPermissionIDs = _.filter(selectedPermissionsKeys, permissionsKey => {
            const isGroupElement = _.get(selectedPermissions, [`${permissionsKey}`, 'isGroupElement']);
            const value = _.get(selectedPermissions, [`${permissionsKey}`, 'value'], DESELECTED_STATE);

            // We should include permission into request payload only if it completes following criteria
            //  1: if domain belongs to LEGACY_PERMISSION_SUPPORT_LEVEL then we can include parent permissions as well if they are partially/fully selected.
            //  2: if domain belongs to PLATFORM_PERMISSION_SUPPORT_LEVEL then we should not include parent's ( group ID's ) even if they are partially/fully selected.
            const shouldIncludePermission =
                supportLevel === LEGACY_PERMISSION_SUPPORT_LEVEL ? value : value && !isGroupElement;

            return shouldIncludePermission;
        });
        if (!_.isEmpty(filteredSelectedPermissionIDs)) {
            if (domain.supportLevel === PLATFORM_PERMISSION_SUPPORT_LEVEL) {
                selectedPlatformPermissions = [...selectedPlatformPermissions, ...filteredSelectedPermissionIDs];
            }
            if (domain.supportLevel === LEGACY_PERMISSION_SUPPORT_LEVEL) {
                selectedLegacyPermissions = [...selectedLegacyPermissions, ...filteredSelectedPermissionIDs];
            }
        }
    });

    // If we are still not ready with checkboxes i,e domains are not loaded yet
    // We are on edit role drawer and we haven't fetched any domains yet
    // Someone changes the role display name and hit save
    // so we use the permissions that are already associated to role without looking for checkboxes
    if (_.isEmpty(selectedLegacyPermissions) && _.isEmpty(selectedPlatformPermissions) && _.isEmpty(domains)) {
        const preAssignedLegacyPermissions = toArray(
            getScreenData(
                Store().getState().screen,
                ['activeRole', 'selectedPermissions', 'legacyPermissions'],
                screenKey
            )
        );
        const preAssignedPlatformPermissions = toArray(
            getScreenData(
                Store().getState().screen,
                ['activeRole', 'selectedPermissions', 'platformPermissions'],
                screenKey
            )
        );

        // modifying data to be in required format
        const preAssignedLegacyPermissionsIDs = _.map(preAssignedLegacyPermissions, element => element.permissionId);
        const preAssignedPlatformPermissionsIDs = _.map(
            preAssignedPlatformPermissions,
            element => element.permissionId
        );
        return {
            legacyPermissions: preAssignedLegacyPermissionsIDs,
            platformPermissions: preAssignedPlatformPermissionsIDs,
        };
    }

    return { legacyPermissions: selectedLegacyPermissions, platformPermissions: selectedPlatformPermissions };
};

export const getUpdatedRolePermissions = (screenKey = ACTIVE_SCREEN_KEY) => {
    const gatheredPermissions = getSelectedPermissions(screenKey);

    // converting gathered permissions to request-body-friendly shape
    const ans = _.reduce(
        gatheredPermissions,
        (result, permissions, supportLevel) => {
            const resultingPermissions = _.map(permissions, permissionId => {
                return { permissionId };
            });

            return {
                ...result,
                [supportLevel]: resultingPermissions,
            };
        },
        {}
    );

    return ans;
};

const getDataInRequiredFormat = (
    node,
    isGroup = false,
    supportLevel = PLATFORM_PERMISSION_SUPPORT_LEVEL,
    defaultState = DESELECTED_STATE
) => {
    return supportLevel === LEGACY_PERMISSION_SUPPORT_LEVEL
        ? {
              ...node,
              value: defaultState,
              isGroupElement: isGroup,
              parentId: node?.parentPermissionId,
              displayName: node?.displayName || node?.name,
          }
        : {
              ...node,
              isGroupElement: isGroup,
              value: defaultState,
              parentId: node?.groupId ?? node?.parentId,
              disabled: isGroup && !node.allowGroupSelection,
              displayName: node?.displayName || node?.name,
          };
};

// This utility function will be used for converting
// domain specification data to tree structure
// which can be fed to Granular Permission component.
// needs some modofication
export const convertDomainInfoToTreeFormat = domainInfo => {
    // initialize tree
    const tree = [];
    const selectedPermissions = {};

    // it will hold reference of each node against id of that node
    const mp = new Map();

    // If support level for domain is 2 that means it only contains permissions that are understood by platform ( new permissions )
    if (domainInfo?.supportLevel === PLATFORM_PERMISSION_SUPPORT_LEVEL) {
        const allPermRef = new Map();
        const parentsSet = new Set();
        // iterate over groups
        _.forEach(domainInfo.groups, group => {
            const groupCopy = getDataInRequiredFormat(group, true, PLATFORM_PERMISSION_SUPPORT_LEVEL);
            parentsSet.add(groupCopy.id);
            mp.set(groupCopy?.id, groupCopy);
            allPermRef[groupCopy.id] = groupCopy;

            if (_.isEmpty(tree) || _.isEmpty(groupCopy?.parentId)) {
                // if tree is empty or group does not have parent group then insert node into tree
                tree.push(groupCopy);
            } else {
                // find parent node in tree using reference from mp
                // append curr node into children of parent node
                const foundNode = mp.get(groupCopy?.parentId);
                if (_.isEmpty(foundNode)) {
                    // if someone by mistake made parent group id as wrong so we can't find that group in our tree
                    tree.push(groupCopy);
                } else if (_.isEmpty(foundNode?.children)) {
                    foundNode.children = [groupCopy];
                } else {
                    foundNode.children.push(groupCopy);
                }
            }
        });

        // iterate over permissions
        _.forEach(domainInfo?.permissions, permission => {
            const permissionCopy = getDataInRequiredFormat(permission, false, PLATFORM_PERMISSION_SUPPORT_LEVEL);
            allPermRef[permissionCopy.id] = permissionCopy;

            // check for group id, find group in tree and insert permission into children of group
            if (!_.isEmpty(permissionCopy?.parentId)) {
                const foundNode = mp.get(permissionCopy?.parentId);

                if (_.isEmpty(foundNode)) {
                    // if someone by mistake made group id as wrong so we can't find that group in our tree
                    tree.push(permissionCopy);
                } else if (_.isEmpty(foundNode?.children)) {
                    foundNode.children = [permissionCopy];
                } else {
                    foundNode.children.push(permissionCopy);
                }
            } else {
                // If permission doesn't have any group just push that in tree
                tree.push(permissionCopy);
            }
        });

        _.forEach(allPermRef, (node, id) => {
            selectedPermissions[id] = getDataInRequiredFormat(
                node,
                parentsSet.has(id),
                PLATFORM_PERMISSION_SUPPORT_LEVEL
            );
        });
    }

    // If support level for domain is 1 that means it only contains permissions that are understood by legacy ( legacy permissions )
    if (domainInfo?.supportLevel === LEGACY_PERMISSION_SUPPORT_LEVEL) {
        const legacyPermissions = _.get(domainInfo, ['legacy', 'permissions']);
        const parentsSet = new Set();
        const allPermRef = new Map();

        // iterate over the permissions
        _.forEach(legacyPermissions, permission => {
            const parentId = _.get(permission, ['parentPermissionId']);

            const isParentIdEmpty = _.isEmpty(parentId);
            if (!isParentIdEmpty) parentsSet.add(parentId);
            const nodeCopy = getDataInRequiredFormat(permission, isParentIdEmpty, LEGACY_PERMISSION_SUPPORT_LEVEL);
            allPermRef[nodeCopy.id] = nodeCopy;

            // insert the permissions in tree that does not have any parent permissions
            // In this way we could identify in first place who all are roots of the tree
            if (isParentIdEmpty) tree.push(nodeCopy);

            // create a map of key=permissionId and value=reference to node
            // Set its ref in map that can be queried by using permission id
            mp.set(permission?.id, nodeCopy);
        });

        // Iterate over the nodes data to establish link between them
        mp.forEach(node => {
            const parentId = _.get(node, ['parentPermissionId']);

            // If parent present then get that parent and attach node to parent
            if (parentId) {
                const parentNode = mp.get(parentId);
                if (_.isEmpty(parentNode?.children)) parentNode.children = [node];
                else parentNode.children.push(node);
            }
        });

        _.forEach(allPermRef, (node, id) => {
            selectedPermissions[id] = getDataInRequiredFormat(
                node,
                parentsSet.has(id),
                LEGACY_PERMISSION_SUPPORT_LEVEL
            );
        });

        // get updated nodes from map that might have been modified with attached children
        const updatedTree = _.map(tree, node => {
            const updatedNode = mp.get(node?.id);

            return updatedNode;
        });

        return { tree: updatedTree, selectedPermissions };
    }

    mp.clear();

    return { tree, selectedPermissions };
};

export const getNumberOfAssignedPermissions = domain => {
    const selectedPermissions = domain?.selectedPermissions ?? {};
    const selectedPermissionsKeys = Object.keys(selectedPermissions);

    return _.reduce(
        selectedPermissionsKeys,
        (acc, curr) => {
            if (selectedPermissions[curr]?.value) return acc + 1;
            return acc;
        },
        0
    );
};

export const cleanupSelectedPermissions = (screenKey = ACTIVE_SCREEN_KEY) => {
    const domains = toArray(getScreenData(Store().getState().screen, ['domains'], screenKey));
    _.forEach(domains, domain => {
        _.forEach(domain.selectedPermissions, permission => {
            Store().dispatch(
                setScreenData(
                    ['domains', domain?.id, 'selectedPermissions', permission.id, 'value'],
                    DESELECTED_STATE,
                    screenKey
                )
            );
        });
    });
};

// get all siblings of permission(view)
// for if given permission(view) is present as depedant for every other sibling(eg - del)
// if present, check if that sibling is selected or not
// get parent node
export const checkIfDependantPermissionSelected = (node, domainId, screenKey) => {
    if (_.isEmpty(node)) return;

    let allSiblings = [];
    if (node.parentId) {
        const parentNode = toMap(
            getScreenData(
                Store().getState().screen,
                ['domains', domainId, 'selectedPermissions', node.parentId],
                screenKey
            )
        );
        allSiblings = _.get(parentNode, ['children'], []);
    } else {
        allSiblings = toMap(
            getScreenData(Store().getState().screen, ['domains', domainId, 'selectedPermissions'], screenKey)
        );
    }

    const mappedSiblingsWithUpdatedValue = _.map(allSiblings, sibling => {
        const val = getScreenData(
            Store().getState().screen,
            ['domains', domainId, 'selectedPermissions', sibling.id, 'value'],
            screenKey
        );
        return {
            ...sibling,
            value: val,
        };
    });

    return _.find(mappedSiblingsWithUpdatedValue, sibling => {
        if (sibling.dependents?.includes(node.id)) {
            return sibling.value === SELECTED_STATE;
        }
        return false;
    });
};

const markDependents = (node, isChecked, domainId, screenKey = ACTIVE_SCREEN_KEY) => {
    if (_.isEmpty(node)) return;

    // On remove permission(del) -> dependent(view) should not be removed
    if (node.value === DESELECTED_STATE) {
        return;
    }

    // Check if there are any dependents
    const dependentSiblings = _.get(node, ['dependents'], []);
    // if yes mark all of them as selected
    _.forEach(dependentSiblings, siblingId => {
        Store().dispatch(
            setScreenData(
                ['domains', domainId, 'selectedPermissions', siblingId, 'value'],
                isChecked ? SELECTED_STATE : DESELECTED_STATE,
                screenKey
            )
        );
    });
};

const updateParentsUtil = (node, domainId, screenKey, isChecked) => {
    if (_.isEmpty(node)) return;
    if (_.isEmpty(node.parentId)) return;

    // get parent node
    const parentNode = toMap(
        getScreenData(Store().getState().screen, ['domains', domainId, 'selectedPermissions', node.parentId], screenKey)
    );

    // get all children of parent ( all siblings )
    const allSiblings = _.get(parentNode, ['children'], []);

    // allSelected=for every sibling check value===SELECTED_STATE
    const allSelected = _.every(allSiblings, sibling => {
        return (
            getScreenData(
                Store().getState().screen,
                ['domains', domainId, 'selectedPermissions', sibling.id, 'value'],
                screenKey
            ) === SELECTED_STATE
        );
    });

    // noneSelected=for every sibling check value===DESELECTED_STATE
    const noneSelected = _.every(allSiblings, sibling => {
        return (
            getScreenData(
                Store().getState().screen,
                ['domains', domainId, 'selectedPermissions', sibling.id, 'value'],
                screenKey
            ) === DESELECTED_STATE
        );
    });

    // if(allSelected) mark parent as SELECTED_STATE
    if (allSelected) {
        Store().dispatch(
            setScreenData(
                ['domains', domainId, 'selectedPermissions', parentNode.id, 'value'],
                SELECTED_STATE,
                screenKey
            )
        );
    }

    // if(noneSelected) mark parent as DESELECTED_STATE
    else if (noneSelected) {
        Store().dispatch(
            setScreenData(
                ['domains', domainId, 'selectedPermissions', parentNode.id, 'value'],
                DESELECTED_STATE,
                screenKey
            )
        );
    }

    // else mark parent as PARTIAL_SELECTED_STATE
    else {
        Store().dispatch(
            setScreenData(
                ['domains', domainId, 'selectedPermissions', parentNode.id, 'value'],
                PARTIAL_SELECTED_STATE,
                screenKey
            )
        );

        // We are in this block that means both of the following conditions have failed :
        //  1) All the siblings are selected
        //  2) None of the siblings are selected
        markDependents(parentNode, isChecked, domainId, screenKey);
    }
    updateParentsUtil(parentNode, domainId, screenKey, isChecked);
};

const updateParents = (node, domainId, screenKey = ACTIVE_SCREEN_KEY, isChecked = false) => {
    updateParentsUtil(node, domainId, screenKey, isChecked);
};

const markDescendentsUtil = (node, isChecked, domainId, screenKey = ACTIVE_SCREEN_KEY) => {
    if (_.isEmpty(node)) return;

    Store().dispatch(
        setScreenData(
            ['domains', domainId, 'selectedPermissions', node.id, 'value'],
            isChecked ? SELECTED_STATE : DESELECTED_STATE,
            screenKey
        )
    );

    if (_.isEmpty(node.children)) return;

    _.forEach(node.children, childNode => {
        markDescendentsUtil(childNode, isChecked, domainId, screenKey);
    });
};

const markDescendents = (node, isChecked, domainId, screenKey = ACTIVE_SCREEN_KEY) => {
    markDescendentsUtil(node, isChecked, domainId, screenKey);
};

export const GenerateUpdates = (screenKey = ACTIVE_SCREEN_KEY) => {
    const initialPermissions = toMap(
        getScreenData(Store().getState().screen, ['activeRole', 'selectedPermissions'], screenKey)
    );
    delete initialPermissions.legacyRoleId;

    const updatedPermissions = getUpdatedRolePermissions(screenKey);

    const sortPermissions = obj => ({
        legacyPermissions: _.sortBy(obj.legacyPermissions, 'permissionId'),
        platformPermissions: _.sortBy(obj.platformPermissions, 'permissionId'),
    });

    const permissionsUpdated = !_.isEqual(sortPermissions(initialPermissions), sortPermissions(updatedPermissions));

    Store().dispatch(setScreenData(['roleUpdates', 'rolePermissions'], permissionsUpdated, screenKey));
};

export const onSelectPermission = (
    node,
    isChecked,
    domainId,
    screenKey = ACTIVE_SCREEN_KEY,
    generateUpdates = false
) => {
    if (_.isEmpty(node)) return;

    // On remove dependent(view) -> If parent permission(del) selected, then it should not allow to remove dependent(view)
    if (!isChecked && checkIfDependantPermissionSelected(node, domainId, screenKey)) {
        return;
    }

    // mark permission as DESELECTED_STATE if unchecked or SELECTED_STATE if checked
    Store().dispatch(
        setScreenData(
            ['domains', domainId, 'selectedPermissions', node.id, 'value'],
            isChecked ? SELECTED_STATE : DESELECTED_STATE,
            screenKey
        )
    );

    const nodeData = toMap(
        getScreenData(Store().getState().screen, ['domains', domainId, 'selectedPermissions', node?.id], screenKey)
    );

    // mark all its descendants as DESELECTED_STATE if unchecked or SELECTED_STATE if checked
    markDescendents(nodeData, isChecked, domainId, screenKey);

    // mark all the dependent node if any
    markDependents(nodeData, isChecked, domainId, screenKey);

    // update its parents state
    updateParents(nodeData, domainId, screenKey, isChecked);

    // we can debounce it instead of calling every time
    if (generateUpdates) {
        GenerateUpdates(screenKey);
    }

    checkIfAllPermissionsSelected(domainId, screenKey);
};

const getCheckboxState = (allSelected, noneSelected) => {
    if (allSelected) return SELECTED_STATE;
    if (noneSelected) return DESELECTED_STATE;
    return PARTIAL_SELECTED_STATE;
};

export const traverseTreeAndMarkSelectedPermissions = (
    domain,
    selectedPermissionsSet,
    screenKey = ACTIVE_SCREEN_KEY
) => {
    if (_.isEmpty(domain?.permissions)) return;

    _.forEach(domain?.selectedPermissions, node => {
        if (selectedPermissionsSet.has(node?.id)) {
            // check if all the children are selected
            const allSelected = _.every(node.children, child => {
                return selectedPermissionsSet.has(child.id);
            });

            // check if none of the children are selected
            const noneSelected = _.every(node.children, child => {
                return !selectedPermissionsSet.has(child.id);
            });

            const value = getCheckboxState(allSelected, noneSelected);

            Store().dispatch(
                setScreenData(['domains', domain?.id, 'selectedPermissions', node?.id, 'value'], value, screenKey)
            );
        }
    });

    if (domain.supportLevel === LEGACY_PERMISSION_SUPPORT_LEVEL) {
        // This case will take care of following condition
        //                       ( A )
        //                          \
        //                          ( B ) --> PARTIALLY SELEECTED
        //                          /   \
        //                        ( C ) ( D ) --> ONLY D IS SELECTED

        // Without following logic A will consider that B is present in selected permissions set
        // But thing is B itself is partially selected.
        // We will iterate one more time on selected permissions and see if one of their children is partially selected.
        // That will help to update the status of parent

        selectedPermissionsSet.forEach(permission => {
            const children = _.get(domain, ['selectedPermissions', permission, 'children'], []);

            // Check if any of the children is partially selected.
            const partialSelectedChild = _.filter(children, child => {
                return (
                    getScreenData(Store().getState().screen, [
                        'domains',
                        domain?.id,
                        'selectedPermissions',
                        child?.id,
                        'value',
                    ]) === PARTIAL_SELECTED_STATE
                );
            });

            // If we found some children are in partially selected state.
            // Update status of parent
            if (!_.isEmpty(partialSelectedChild))
                Store().dispatch(
                    setScreenData(
                        ['domains', domain?.id, 'selectedPermissions', permission, 'value'],
                        PARTIAL_SELECTED_STATE,
                        screenKey
                    )
                );
        });
    } else if (domain.supportLevel === PLATFORM_PERMISSION_SUPPORT_LEVEL) {
        // In case of PLATFORM_PERMISSIONS
        // We can't figure out state of parent groups just by looking at selected permission set.
        // We will update parents of each selected permission.
        selectedPermissionsSet.forEach(permission => {
            updateParents(_.get(domain, ['selectedPermissions', permission], {}), domain.id, screenKey);
        });
    }
    checkIfAllPermissionsSelected(domain.id, screenKey);
};

export const sortPermissions = nodes => {
    if (!nodes || nodes.length === 0) {
        return [];
    }

    // Sort the current level of permissions alphabetically by `displayName`
    const sortedNodes = [...nodes].sort((a, b) => {
        if (a.displayName < b.displayName) return -1;
        if (a.displayName > b.displayName) return 1;
        return 0;
    });

    // Recursively sort the children of each node
    return sortedNodes.map(node => {
        if (node.children) {
            return {
                ...node,
                children: sortPermissions(node.children),
            };
        }
        return node;
    });
};

export default onSelectPermission;
